import { useCallback, useEffect, useState } from 'react';
import { api, formatWib, hapusTerkonfirmasi } from '../lib/api';
import { DialogHapus } from '../components/DialogHapus';
import { Pager, usePaging } from '../components/Pager';
import { useAuth, type UserRole } from '../hooks/useAuth';
import { useBahasa } from '../i18n';

interface UserRow {
  id: string;
  username: string;
  email: string | null;
  fullName: string;
  authSource: 'local' | 'ldap';
  role: UserRole;
  isActive: boolean;
  lockedUntil: string | null;
  isLocked: boolean;
  lastLoginAt: string | null;
  hasPassword: boolean;
}

const MIN_PASSWORD = 12;
/** 46 user dan bertambah — tabel penuh memaksa menggulung jauh ke tombol di bawah. */
const PER_PAGE = 20;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 rounded-lg border border-line bg-surface p-4">
      <h2 className="mb-3 text-lg">{title}</h2>
      {children}
    </section>
  );
}

export function UsersPage() {
  const { user: saya } = useAuth();
  const { t } = useBahasa();
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [pesan, setPesan] = useState<{ jenis: 'ok' | 'gagal'; teks: string } | null>(null);
  /** Admin yang tautan akunnya sedang dibuka (§F7.1). */
  const [menautkan, setMenautkan] = useState<UserRow | null>(null);
  /** User yang sedang dikonfirmasi penghapusannya (§G17). */
  const [akanDihapus, setAkanDihapus] = useState<UserRow | null>(null);

  const [baru, setBaru] = useState({
    username: '',
    email: '',
    fullName: '',
    role: 'viewer' as UserRole,
    password: '',
  });

  const muat = useCallback(async () => {
    try {
      const u = await api<{ users: UserRow[] }>('/users');
      setUsers(u.users);
    } catch (e) {
      setPesan({ jenis: 'gagal', teks: (e as Error).message });
    }
  }, []);

  useEffect(() => {
    void muat();
  }, [muat]);

  async function jalankan(fn: () => Promise<unknown>, sukses: string) {
    setPesan(null);
    try {
      await fn();
      setPesan({ jenis: 'ok', teks: sukses });
      await muat();
    } catch (e) {
      setPesan({ jenis: 'gagal', teks: (e as Error).message });
    }
  }

  async function resetPassword(u: UserRow) {
    const pw = window.prompt(
      t('New password for {username} (at least {n} characters):', {
        username: u.username,
        n: MIN_PASSWORD,
      }),
    );
    if (!pw) return;
    if (pw.length < MIN_PASSWORD) {
      setPesan({ jenis: 'gagal', teks: `Password must be at least ${MIN_PASSWORD} characters.` });
      return;
    }
    await jalankan(
      () => api(`/users/${u.id}/password`, { method: 'POST', body: JSON.stringify({ password: pw }) }),
      t('Password changed for {username}. All of their sessions were signed out.', {
        username: u.username,
      }),
    );
  }

  const paging = usePaging(users ?? [], PER_PAGE);

  if (!users) {
    return (
      <p className="p-8 text-sm text-muted" role="status">
        {t('Loading users…')}
      </p>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4">
      <h1 className="mb-4 text-xl">{t('Users')}</h1>

      {pesan && (
        <p
          role={pesan.jenis === 'gagal' ? 'alert' : 'status'}
          className={`mb-4 rounded-md border px-3 py-2 text-sm ${
            pesan.jenis === 'gagal' ? 'border-amber-500 text-amber-600' : 'border-line text-muted'
          }`}
        >
          {pesan.teks}
        </p>
      )}

      <Section title={t('Users')}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr className="border-b border-line">
                <th className="py-2 font-medium">{t('Username')}</th>
                <th className="py-2 font-medium">{t('Name')}</th>
                <th className="py-2 font-medium">{t('Source')}</th>
                <th className="py-2 font-medium">{t('Role')}</th>
                <th className="py-2 font-medium">{t('Status')}</th>
                <th className="py-2 font-medium">{t('Last sign-in')}</th>
                <th className="py-2 text-right font-medium">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paging.slice.map((u) => (
                <tr key={u.id} className="border-b border-line">
                  <td className="py-2">
                    {u.username}
                    {u.email && <div className="text-xs text-muted">{u.email}</div>}
                  </td>
                  <td className="py-2">{u.fullName}</td>
                  <td className="py-2 text-muted">{u.authSource}</td>
                  <td className="py-2">
                    <select
                      value={u.role}
                      onChange={(e) =>
                        void jalankan(
                          () =>
                            api(`/users/${u.id}`, {
                              method: 'PUT',
                              body: JSON.stringify({ role: e.target.value }),
                            }),
                          t('Role updated for {username}.', { username: u.username }),
                        )
                      }
                      className="min-h-tap rounded-md border border-line bg-surface px-2"
                    >
                      <option value="viewer">{t('User')}</option>
                      <option value="operator">{t('Operator')}</option>
                      <option value="admin">{t('Administrator')}</option>
                    </select>
                  </td>
                  <td className="py-2">
                    {!u.isActive ? (
                      <span className="text-muted">{t('inactive')}</span>
                    ) : u.isLocked ? (
                      <span className="text-amber-600" title={u.lockedUntil ?? undefined}>
                        {t('locked')}
                      </span>
                    ) : (
                      <span className="text-green-600">{t('active')}</span>
                    )}
                  </td>
                  <td className="py-2 text-muted">
                    {u.lastLoginAt ? formatWib(u.lastLoginAt) : '—'}
                  </td>
                  <td className="py-2">
                    <div className="flex flex-wrap justify-end gap-1">
                      {/* User LDAP tidak punya password lokal — tombolnya tidak
                          ditampilkan sama sekali, bukan ditampilkan lalu gagal. */}
                      {u.authSource === 'local' && (
                        <button
                          type="button"
                          onClick={() => void resetPassword(u)}
                          className="min-h-tap rounded-md border border-line px-2 hover:bg-subtle"
                        >
                          {t('Reset password')}
                        </button>
                      )}
                      {u.isLocked && (
                        <button
                          type="button"
                          onClick={() =>
                            void jalankan(
                              () => api(`/users/${u.id}/unlock`, { method: 'POST' }),
                              t('{username} was unlocked.', { username: u.username }),
                            )
                          }
                          className="min-h-tap rounded-md border border-line px-2 hover:bg-subtle"
                        >
                          {t('Unlock')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          void jalankan(
                            () =>
                              api(`/users/${u.id}`, {
                                method: 'PUT',
                                body: JSON.stringify({ isActive: !u.isActive }),
                              }),
                            u.isActive
                              ? t('{username} was deactivated.', { username: u.username })
                              : t('{username} was activated.', { username: u.username }),
                          )
                        }
                        className="min-h-tap rounded-md border border-line px-2 hover:bg-subtle"
                      >
                        {u.isActive ? t('Deactivate') : t('Activate')}
                      </button>
                      {/* Tautan akun hanya berlaku untuk admin (§F7.1) — akun
                          viewer tidak pernah jadi induk, jadi tombolnya tidak
                          ditampilkan alih-alih ditampilkan lalu ditolak server. */}
                      {u.role === 'admin' && u.isActive && (
                        <button
                          type="button"
                          onClick={() => setMenautkan(menautkan?.id === u.id ? null : u)}
                          aria-expanded={menautkan?.id === u.id}
                          className="min-h-tap rounded-md border border-line px-2 hover:bg-subtle"
                        >
                          {t('Linked accounts')}
                        </button>
                      )}
                      {u.id !== saya?.id && (
                        <button
                          type="button"
                          onClick={() => setAkanDihapus(u)}
                          className="min-h-tap rounded-md border border-red-600 px-2 hover:bg-subtle"
                        >
                          {t('Delete')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {menautkan && (
          <PanelTautan
            parent={menautkan}
            users={users ?? []}
            onClose={() => setMenautkan(null)}
            onSaved={(teks) => {
              setPesan({ jenis: 'ok', teks });
              setMenautkan(null);
            }}
            onError={(teks) => setPesan({ jenis: 'gagal', teks })}
          />
        )}

        {akanDihapus && (
          <DialogHapus
            nama={akanDihapus.username}
            judul={t('Delete {username}', { username: akanDihapus.username })}
            keterangan={t(
              'The account, its sessions, and everything it owns personally — bookmarks and custom dashboards — are gone. Recordings and audit history stay.',
            )}
            onBatal={() => setAkanDihapus(null)}
            onHapus={async (k) => {
              await hapusTerkonfirmasi(`/users/${akanDihapus.id}`, k);
              setAkanDihapus(null);
              setPesan({
                jenis: 'ok',
                teks: t('{username} was deleted.', { username: akanDihapus.username }),
              });
              await muat();
            }}
          />
        )}

        <Pager
          page={paging.page}
          pageCount={paging.pageCount}
          start={paging.start}
          shown={paging.slice.length}
          total={paging.total}
          noun="users"
          onPage={paging.setPage}
        />

        <div className="mt-4 border-t border-line pt-4">
          <h3 className="mb-2 text-sm font-medium">{t('Add a local user')}</h3>
          <div className="flex flex-wrap items-end gap-2">
            {(
              [
                ['username', 'Username', 'text'],
                ['fullName', 'Full name', 'text'],
                ['email', 'Email (optional)', 'email'],
              ] as const
            ).map(([key, label, type]) => (
              <label key={key} className="flex flex-col gap-1 text-sm">
                {t(label)}
                <input
                  type={type}
                  value={baru[key]}
                  onChange={(e) => setBaru({ ...baru, [key]: e.target.value })}
                  className="min-h-tap w-44 rounded-md border border-line bg-surface px-3"
                />
              </label>
            ))}
            <label className="flex flex-col gap-1 text-sm">
              {t('Role')}
              <select
                value={baru.role}
                onChange={(e) => setBaru({ ...baru, role: e.target.value as UserRole })}
                className="min-h-tap rounded-md border border-line bg-surface px-2"
              >
                <option value="viewer">{t('User')}</option>
                <option value="operator">{t('Operator')}</option>
                <option value="admin">{t('Administrator')}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Password (min. {MIN_PASSWORD} characters)
              <input
                type="password"
                value={baru.password}
                onChange={(e) => setBaru({ ...baru, password: e.target.value })}
                className="min-h-tap w-52 rounded-md border border-line bg-surface px-3"
              />
            </label>
            <button
              type="button"
              disabled={
                !baru.username.trim() ||
                !baru.fullName.trim() ||
                baru.password.length < MIN_PASSWORD
              }
              onClick={() =>
                void jalankan(async () => {
                  await api('/users', {
                    method: 'POST',
                    body: JSON.stringify({
                      ...baru,
                      email: baru.email.trim() || null,
                    }),
                  });
                  setBaru({ username: '', email: '', fullName: '', role: 'viewer', password: '' });
                }, t('User created.'))
              }
              className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.96] disabled:opacity-40"
            >
              {t('Add')}
            </button>
          </div>
        </div>
      </Section>

    </div>
  );
}

/**
 * Daftar akun yang boleh dimasuki seorang admin tanpa password (§F7.1).
 *
 * Disimpan sekali sebagai satu daftar, bukan satu permintaan per centang: ini
 * pengaturan yang menentukan siapa bisa jadi siapa, dan daftar yang tersimpan
 * separuh karena permintaan kedua gagal adalah keadaan yang tidak boleh ada.
 * Karena itu ada tombol Save yang jelas, dan Cancel yang benar-benar membatalkan.
 */
function PanelTautan({
  parent,
  users,
  onClose,
  onSaved,
  onError,
}: {
  parent: UserRow;
  users: UserRow[];
  onClose: () => void;
  onSaved: (teks: string) => void;
  onError: (teks: string) => void;
}) {
  const { t } = useBahasa();
  const [dipilih, setDipilih] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let hidup = true;
    setDipilih(null);
    void api<{ childIds: string[] }>(`/users/${parent.id}/links`)
      .then((r) => hidup && setDipilih(new Set(r.childIds)))
      .catch((err) => hidup && onError((err as Error).message));
    return () => {
      hidup = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent.id]);

  // Hanya viewer aktif yang bisa jadi anak — pagar yang sama dengan server,
  // supaya daftarnya tidak pernah memuat nama yang akan ditolak saat disimpan.
  const kandidat = users.filter((u) => u.role === 'viewer' && u.isActive);

  async function simpan() {
    if (!dipilih) return;
    setBusy(true);
    try {
      await api(`/users/${parent.id}/links`, {
        method: 'PUT',
        body: JSON.stringify({ childIds: [...dipilih] }),
      });
      onSaved(`Linked accounts updated for ${parent.username}.`);
    } catch (err) {
      onError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-line p-3">
      <h3 className="text-base">Accounts {parent.username} can open</h3>
      <p className="mt-1 text-xs text-muted">
        {t('{username} can switch into these without a password. Coming back always asks for theirs.', {
          username: parent.username,
        })}
      </p>

      {dipilih === null ? (
        <p className="mt-3 text-sm text-muted">{t('Loading…')}</p>
      ) : kandidat.length === 0 ? (
        <p className="mt-3 text-sm text-muted">
          {t('There are no active viewer accounts to link.')}
        </p>
      ) : (
        <ul className="mt-3 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {kandidat.map((u) => (
            <li key={u.id}>
              <label className="flex min-h-tap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={dipilih.has(u.id)}
                  onChange={(e) =>
                    setDipilih((cur) => {
                      const next = new Set(cur);
                      if (e.target.checked) next.add(u.id);
                      else next.delete(u.id);
                      return next;
                    })
                  }
                />
                <span className="min-w-0 truncate">
                  {u.fullName} <span className="text-muted">· {u.username}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => void simpan()}
          disabled={busy || dipilih === null}
          className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast disabled:opacity-60"
        >
          {busy ? t('Saving…') : t('Save links')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="min-h-tap rounded-md border border-line px-4 text-sm hover:bg-subtle"
        >
          {t('Cancel')}
        </button>
      </div>
    </div>
  );
}
